import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@/components/molecules/Grid';
import { ModalTitle, PopupInnerVerticalStretch } from '@/components/atoms/UI/Modal/styles';
import CopyTextInside from '@/components/features/CopyTextInside/CopyTextInside';
import { AnimatedAppearance, basicColor } from '@/styles/template';
import Button from '@/components/atoms/UI/buttons/Button';
import Icon from '@/components/atoms/UI/Icon/Icon';
import { BoldTextS, TextS } from '@/pages/designSystem/Fonts';
import IconButton from '@/components/atoms/UI/buttons/IconButton';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { selectTestTitle } from '@/store/selectors/resultSelectors';
import {
	selectAllowDomains,
	selectErrorDomainsList,
	selectIsFetchingDomainsList
} from '@/store/selectors/domainsListSelector';
import { selectErrorWidgetKey, selectWidgetKey } from '@/store/selectors/widgetKeySelector';
import { sendDataToGenerate } from '@/store/reducers/createWidgetSlice';
import { AppDispatch } from '@/store/store';
import LoadingErrorHandler from '@/components/templates/LoadingErrorHandler';
import { selectIsWidgetExisting } from '@/store/selectors/widgetExistingSelector';
import { fetchDomainsListData } from '@/store/reducers/domainsListSlice';
import { ROUTES } from '@/constants/urls';

export const GeneratedCode = styled.div`
  ${AnimatedAppearance};
  width: 100%;
`;

type WidgetModalProps = {
	onManualClick: () => void;
};

const WidgetModalGenerateCode: React.FC<WidgetModalProps> = ({ onManualClick }) => {
	const dispatch: AppDispatch = useDispatch();
	const isWidgetExisting = useSelector(selectIsWidgetExisting);

	const textTitle = useSelector(selectTestTitle);
	const domainList = useSelector(selectAllowDomains);
	const widgetKey = useSelector(selectWidgetKey);
	const error = useSelector(selectErrorWidgetKey);

	const domainsError = useSelector(selectErrorDomainsList);
	const isFetchingDomainsList = useSelector(selectIsFetchingDomainsList);

	const { t } = useTranslation();
	const text_title = t('embedTest');
	const text_to_site = t('widget.to_sites');
	const text_instruction = t('instruction');
	const text_your_widgets = t('widget.your_widgets');
	const text_generate = t('widget.generate');

	const [codeGenerated, setCodeGenerated] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);

	const handleGenerateCode = async () => {
		setIsGenerating(true);
		const success = await dispatch(sendDataToGenerate());

		if (success) {
			setCodeGenerated(true);
		}

		setIsGenerating(false);
	};

	useEffect(() => {
		if (isWidgetExisting) {
			setCodeGenerated(true)
		}
	}, [isWidgetExisting]);

	return (
		<PopupInnerVerticalStretch>
			<Grid col='1' gap='3' centeredContent>
				<ModalTitle>{text_title}</ModalTitle>
				<BoldTextS>«{textTitle}»</BoldTextS>

				<Grid col='1' gap='1' centeredContent>
					<TextS>{text_to_site}:</TextS>

					<LoadingErrorHandler error={domainsError} isFetching={isFetchingDomainsList}>
						{domainList.length > 0 && (
							<Grid col={'1'} gap={'1'} centeredContent>
								{domainList.map((domain) => (
									<div key={domain}>
										<BoldTextS>{domain}</BoldTextS>
									</div>
								))}
							</Grid>
						)}
					</LoadingErrorHandler>

					<IconButton faint href={ROUTES.WIDGETS}>
						<Icon name='Edit' />
					</IconButton>
				</Grid>

				{!isWidgetExisting && !codeGenerated && !isGenerating && (
					<Button onClick={handleGenerateCode}>{text_generate}</Button>
				)}

				{
					codeGenerated && (
						<GeneratedCode>
							<Grid col='1' gap='1' centeredContent>
								<CopyTextInside>
		                            <pre>
		                                <code>
		                                    <span style={{ color: basicColor.primary, fontWeight: 'bold' }}>&lt;div</span>{' '}
		                                    <span style={{ color: basicColor.primary }}>className=</span>
		                                    <span style={{ color: basicColor.red }}>&quot;testometrika_widget&quot;</span>{' '}
		                                    <span style={{ color: basicColor.primary }}>id=</span>
		                                    <span style={{ color: basicColor.red }}>&quot;{widgetKey}&quot;</span>
		                                    <span style={{ color:  basicColor.primary, fontWeight: 'bold' }}>&gt;&lt;/div&gt;</span>
		                                </code>
		                            </pre>
								</CopyTextInside>
								<Button link autoWidth>
									{text_your_widgets}
									<Icon name={'Next'} small />
								</Button>
							</Grid>
						</GeneratedCode>
					)
				}
				<LoadingErrorHandler error={error} isFetching={isGenerating}/>
			</Grid>

			<Grid col='1' gap='1' centeredContent>
				<Button link onClick={onManualClick}>
					<Icon name={'Info'} small />
					{text_instruction}
				</Button>
			</Grid>
		</PopupInnerVerticalStretch>
	);
};

export default WidgetModalGenerateCode;
